import { apiClient, isOfflineError, isRequestCancelled } from './api-client'

type Source = 'boundary' | 'error' | 'unhandledrejection'

const MAX_REPORTS = 10
const NETWORK_NOISE = /^(Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.|The network connection was lost\.|cancelled)$/
const MAX_TRIES_PER_REPORT = 2 // first send + one retry if it failed
const tries = new Map<string, number>() // key -> POSTs attempted; at most MAX_REPORTS keys
const blocked = new Set<string>() // keys sent or in flight
let installed = false

function describe(error: unknown): string {
  const m = (error as { message?: unknown } | null)?.message
  if (typeof m === 'string') return m
  if (typeof error === 'string') return error
  // Objects: type + key names only — values may carry tokens or owner data.
  if (error !== null && typeof error === 'object') {
    return `${Object.prototype.toString.call(error)} keys=${Object.keys(error).join(',')}`
  }
  return String(error)
}

export function reportClientError(source: Source, error: unknown): void {
  try {
    if (navigator.onLine === false) return
    if (isOfflineError(error) || isRequestCancelled(error)) return

    const message = describe(error)
    if (error instanceof TypeError && NETWORK_NOISE.test(message)) return
    if (message === 'Script error.' || /ResizeObserver loop/.test(message)) return

    const key = `${source}:${message}`
    const n = tries.get(key) ?? 0
    if (blocked.has(key) || n >= MAX_TRIES_PER_REPORT || (n === 0 && tries.size >= MAX_REPORTS)) return
    tries.set(key, n + 1)
    blocked.add(key)

    void apiClient
      .post('/client-errors', {
        source,
        message: message.slice(0, 1000),
        stack: error instanceof Error ? error.stack?.slice(0, 4000) : undefined,
        url: (location.origin + location.pathname.replace(/^\/rfi\/[^/]+/, '/rfi/:token')).slice(0, 500),
        userAgent: navigator.userAgent.slice(0, 300),
      })
      .catch(() => {
        blocked.delete(key) // unsent: allow a retry on the next occurrence
      })
  } catch {
    // reporting must never throw
  }
}

export function installGlobalErrorReporting(): void {
  if (installed) return
  installed = true
  window.addEventListener('error', e => reportClientError('error', e.error ?? e.message))
  window.addEventListener('unhandledrejection', e => reportClientError('unhandledrejection', e.reason))
}
